import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { GatedAssetForm } from '@/components/forms/GatedAssetForm';
import { FileArchive, CheckCircle2, UserCheck } from 'lucide-react';

export function ToolkitPersonalMasteryPage() {
  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground relative">
      <Helmet>
        <title>Personal Mastery Assessment Toolkit | FIVE LENS Enneagram</title>
        <meta name="description" content="Self-reflection worksheets for leaders, coaches and HR professionals to baseline personal mastery and emotional regulation, with a 30-day development plan." />
        <meta property="og:title" content="Personal Mastery Assessment Toolkit | FIVE LENS Enneagram" />
        <meta property="og:description" content="Self-reflection worksheets for leaders, coaches and HR professionals to baseline personal mastery and emotional regulation, with a 30-day development plan." />
        <link rel="canonical" href="https://www.fivelensenneagram.com/resource-library/personal-mastery-assessment-toolkit" />
      </Helmet>

      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: 'Resource Library', href: '/resource-library' },
          { label: 'Personal Mastery Assessment Toolkit' }
        ]} />

        <div className="mt-8 mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-rule-3 bg-ground-alt border border-ground-deep px-3 py-1 mb-4 rounded-sm">
            <FileArchive className="w-3.5 h-3.5" /> Toolkit &amp; Worksheets
          </div>
          <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-6">
            Personal Mastery Assessment Toolkit
          </h1>
          <p className="text-xl text-ink/80 max-w-3xl leading-relaxed font-light">
            Self-reflection worksheets for leaders, coaches and HR professionals to baseline personal mastery and emotional regulation, with a 30-day development plan.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Details */}
          <div className="lg:col-span-7 space-y-8">
            <div className="bg-white border border-ground-deep p-8 shadow-sm">
              <h2 className="text-xl font-serif text-ink mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-rule-3" /> Who it&apos;s for
              </h2>
              <p className="text-ink/80 text-sm leading-relaxed">
                Individual leaders, coaches, HR and L&amp;D professionals.
              </p>
            </div>

            <div className="bg-white border border-ground-deep p-8 shadow-sm">
              <h2 className="text-xl font-serif text-ink mb-6">What&apos;s inside</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    A 21-statement self-reflection baseline across seven areas: conscious intent, emotional agility, systemic awareness, authentic alignment, flexibility, emotional stability and self-efficacy
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    A guide to reading your scores, and choosing one area to focus on
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    Four worksheets: a trigger log, a setback review, a values gap check and a 30-day development plan
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    A half-day session plan and ground rules for HR, L&amp;D and coaches running it with a group
                  </span>
                </li>
              </ul>
              
              <div className="mt-8 pt-6 border-t border-ground-deep text-xs text-ink/70 italic">
                A structured reflection tool, not a validated psychometric assessment.
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="lg:col-span-5 bg-white border border-ground-deep p-8 shadow-md sticky top-32">
            <h3 className="text-2xl font-serif text-ink mb-2">Get the Toolkit</h3>
            <p className="text-xs text-ink/70 mb-6 leading-relaxed">
              Complete the form below to instantly access the PDF toolkit download.
            </p>
            <GatedAssetForm 
              assetName="Personal Mastery Assessment Toolkit" 
              mailchimpTag="Asset: Personal Mastery Toolkit" 
              buttonText="Request Toolkit PDF" 
              eventName="toolkit_download_request" 
              ctaPosition="personal_mastery_toolkit_page" 
              downloadUrl="/downloads/Personal-Mastery-Assessment-Toolkit.pdf" 
              consentLabel="Email me the toolkit, plus occasional FIVE LENS insights and event invitations."
            />
          </div>
        </div>
      </div>
    </main>
  );
}
